// Updated Frontend Code (Developer.jsx)

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { ClipLoader } from "react-spinners";
import styles from "./styles/Developer.module.css";

const Developer = () => {
  // Parse full auth data from localStorage - backend gets user.id from JWT token
  const authData = JSON.parse(localStorage.getItem("authData") || "{}");
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const userId = authData.user?.id; // For frontend validation only, not sent to backend

  // Debug function (remove in production)
  const debugAuth = () => {
    console.log("=== AUTH DEBUG ===");
    console.log("Full Auth Data:", authData);
    console.log("Token:", token ? "Present" : "Missing");
    console.log("Role:", role);
    console.log("User ID (local):", userId);
    console.log("==================");
  };

  const [developerData, setDeveloperData] = useState({
    name: "",
    email: "",
    phonenumber: "",
    Region: "",
    Status: "Active",
  });

  const [editDeveloperData, setEditDeveloperData] = useState({
    name: "",
    email: "",
    phonenumber: "",
    Region: "",
    Status: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editDeveloperId, setEditDeveloperId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Backend expects req.user.id from JWT token, so no query params needed
  const getDevelopersEndpoint =
    role === "participant"
      ? "https://prod.toxboxai.com/api/campaign/assigned-developers"
      : "https://prod.toxboxai.com/api/developer/getalldevelopers";

  const fetchDevelopers = async () => {
    if (!token) {
      setError("No authentication token found. Please login again.");
      return;
    }

    try {
      console.log("Fetching developers from:", getDevelopersEndpoint);

      const response = await fetch(getDevelopersEndpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      console.log("Fetch developers response:", result);

      if (response.ok) {
        setDevelopers(result.data?.developers || result.data || []);
        setError("");
      } else {
        setError(result.error || "Failed to fetch developers");
        if (result.error?.includes("Unauthorized")) {
          // Token might be invalid/expired, clear localStorage
          localStorage.removeItem("authData");
          localStorage.removeItem("token");
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error("Fetch developers error:", error);
      setError("Error fetching developers: " + error.message);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDevelopers();
    } else {
      setError("No authentication token found. Please login again.");
    }
  }, [token, role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDeveloperData(prev => ({
      ...prev,
      [name]: value
    }));
    setError("");
  };

  const handleToggleChange = () => {
    setDeveloperData(prev => ({
      ...prev,
      Status: prev.Status === 'Active' ? 'Inactive' : 'Active'
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditDeveloperData(prev => ({
      ...prev,
      [name]: value
    }));
    setError("");
  };

  const validateForm = (data) => {
    if (!data.name.trim()) return "Name is required";
    if (!data.email.trim()) return "Email is required";
    if (!data.phonenumber.trim()) return "Phone number is required";
    if (!/^[0-9]{10}$/.test(data.phonenumber)) return "Phone number must be exactly 10 digits";
    if (!data.Region.trim()) return "Region is required";
    if (!data.Status) return "Status is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Invalid email format";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const validationError = validateForm(developerData);
    if (validationError) {
      setError(validationError);
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      setError("You must be logged in to create a developer");
      setIsSubmitting(false);
      return;
    }

    if (role === "participant") {
      setError("Participants are not authorized to create developers");
      setIsSubmitting(false);
      return;
    }

    try {
      // Backend gets vendorId from req.user.id (JWT token), not from payload
      const payload = {
        name: developerData.name.trim(),
        email: developerData.email.trim(),
        phonenumber: developerData.phonenumber.trim(),
        Region: developerData.Region.trim(),
        Status: developerData.Status,
      };

      console.log("Create Developer Payload:", payload);

      const response = await fetch(
        "https://prod.toxboxai.com/api/developer/createdeveloper",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      console.log("Create developer response:", result);

      setIsSubmitting(false);

      if (response.ok) {
        setSuccess(true);
        setDeveloperData({
          name: "",
          email: "",
          phonenumber: "",
          Region: "",
          Status: "",
        });
        setTimeout(() => {
          setSuccess(false);
          setIsModalOpen(false);
          fetchDevelopers();
        }, 1500);
      } else {
        setError(result.error || "Failed to create developer");
        if (result.error?.includes("Unauthorized")) {
          localStorage.removeItem("authData");
          localStorage.removeItem("token");
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error("Create developer error:", error);
      setIsSubmitting(false);
      setError("Error creating developer: " + error.message);
    }
  };

  const handleEdit = (developer) => {
    setEditDeveloperData({
      name: developer.name || "",
      email: developer.email || "",
      phonenumber: developer.phoneNumber || developer.phonenumber || "",
      Region: developer.Region || "",
      Status: developer.Status || "",
    });
    setEditDeveloperId(developer._id);
    setIsEditModalOpen(true);
    setError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const validationError = validateForm(editDeveloperData);
    if (validationError) {
      setError(validationError);
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      setError("You must be logged in to update a developer");
      setIsSubmitting(false);
      return;
    }

    if (role === "participant") {
      setError("Participants are not authorized to update developers");
      setIsSubmitting(false);
      return;
    }

    if (!editDeveloperId?.match(/^[0-9a-fA-F]{24}$/)) {
      setError("Invalid developer ID");
      setIsSubmitting(false);
      return;
    }

    try {
      // Backend gets vendorId from req.user.id (JWT token), not from payload
      const payload = {
        name: editDeveloperData.name.trim(),
        email: editDeveloperData.email.trim(),
        phonenumber: editDeveloperData.phonenumber.trim(),
        Region: editDeveloperData.Region.trim(),
        Status: editDeveloperData.Status,
      };

      console.log("Update Developer Payload:", payload);

      const response = await fetch(
        `https://prod.toxboxai.com/api/developer/update/${editDeveloperId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      console.log("Update developer response:", result);

      setIsSubmitting(false);

      if (response.ok) {
        setSuccess(true);
        setEditDeveloperData({
          name: "",
          email: "",
          phonenumber: "",
          Region: "",
          Status: "",
        });
        setEditDeveloperId(null);
        setTimeout(() => {
          setSuccess(false);
          setIsEditModalOpen(false);
          fetchDevelopers();
        }, 1500);
      } else {
        setError(result.error || "Failed to update developer");
        if (result.error?.includes("Unauthorized")) {
          localStorage.removeItem("authData");
          localStorage.removeItem("token");
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error("Update developer error:", error);
      setIsSubmitting(false);
      setError("Error updating developer: " + error.message);
    }
  };

  const handleDelete = async (developerId) => {
    if (!window.confirm("Are you sure you want to delete this developer?")) return;

    if (!token) {
      setError("You must be logged in to delete a developer");
      return;
    }

    if (role === "participant") {
      setError("Participants are not authorized to delete developers");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Deleting developer:", developerId);

      const response = await fetch(
        `https://prod.toxboxai.com/api/developer/delete/${developerId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      console.log("Delete developer response:", result);

      setIsSubmitting(false);

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          fetchDevelopers();
        }, 1500);
      } else {
        setError(result.error || "Failed to delete developer");
        if (result.error?.includes("Unauthorized")) {
          localStorage.removeItem("authData");
          localStorage.removeItem("token");
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error("Delete developer error:", error);
      setIsSubmitting(false);
      setError("Error deleting developer: " + error.message);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredDevelopers = developers.filter((developer) =>
    developer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    developer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDeveloperData({
      name: "",
      email: "",
      phonenumber: "",
      Region: "",
      Status: "",
    });
    setError("");
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditDeveloperData({
      name: "",
      email: "",
      phonenumber: "",
      Region: "",
      Status: "",
    });
    setEditDeveloperId(null);
    setError("");
  };



  return (
    <div className={styles.container}>
      {/* Debug button - Remove in production */}
      <h3>
        {role === "participant" ? "Assigned Developers" : "All Developers"}
      </h3>

      {error && (
        <motion.div
          className={styles.error}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          className={styles.successMessage}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          ✓ Developer operation completed successfully!
        </motion.div>
      )}

      {role !== "participant" && (
        <div className={styles.controls}>
          <input
            type="text"
            placeholder="Search developers by name or email..."
            value={searchQuery}
            onChange={handleSearch}
            className={styles.searchInput}
          />
          <button
            className={styles.createBtn}
            onClick={() => setIsModalOpen(true)}
            disabled={isSubmitting}
          >
            Create Developer
          </button>
        </div>
      )}

      <AnimatePresence>
        {isModalOpen && role !== "participant" && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className={styles.closeBtn} onClick={handleCloseModal}>
                ×
              </button>
              <div className={styles.formContainer}>
                <h3>Create Developer</h3>
                {error && (
                  <motion.p
                    className={styles.error}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {error}
                  </motion.p>
                )}
                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={developerData.name}
                        onChange={handleChange}
                        placeholder="Enter developer name"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={developerData.email}
                        onChange={handleChange}
                        placeholder="Enter email address"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        name="phonenumber"
                        value={developerData.phonenumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleChange({ target: { name: 'phonenumber', value } });  // Fixed: Update state directly without mutating e.target.value
                        }}
                        placeholder="Enter 10-digit phone number"
                        maxLength="10"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Region *</label>
                      <input
                        type="text"
                        name="Region"
                        value={developerData.Region}
                        onChange={handleChange}
                        placeholder="Enter region"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.toggleLabel}>
                        Status <span className={styles.required}>*</span>
                      </label>

                      <div className={styles.toggleWrapper}>
                        <span className={`${styles.toggleText} ${developerData.Status === 'Inactive' ? styles.active : ''}`}>
                          Inactive
                        </span>

                        {/* The actual toggle */}
                        <label className={styles.switch}>
                          <input
                            type="checkbox"
                            checked={developerData.Status === 'Active'}
                            onChange={handleToggleChange}
                            required={developerData.Status === ''} // optional: force selection
                          />
                          <span className={`${styles.slider} ${styles.round}`}></span>
                        </label>

                        <span className={`${styles.toggleText} ${developerData.Status === 'Active' ? styles.active : ''}`}>
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <ClipLoader size={20} color="#fff" />
                        Creating...
                      </>
                    ) : (
                      "Create Developer"
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}

        {isEditModalOpen && role !== "participant" && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCloseEditModal}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className={styles.closeBtn} onClick={handleCloseEditModal}>
                ×
              </button>
              <div className={styles.formContainer}>
                <h3>Edit Developer</h3>
                {error && (
                  <motion.p
                    className={styles.error}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {error}
                  </motion.p>
                )}
                <form onSubmit={handleEditSubmit} className={styles.form}>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={editDeveloperData.name}
                        onChange={handleEditChange}
                        placeholder="Enter developer name"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={editDeveloperData.email}
                        onChange={handleEditChange}
                        placeholder="Enter email address"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        name="phonenumber"
                        value={editDeveloperData.phonenumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleEditChange({ target: { name: 'phonenumber', value } });  // Fixed: Update state directly without mutating e.target.value
                        }}
                        placeholder="Enter 10-digit phone number"
                        maxLength="10"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Region *</label>
                      <input
                        type="text"
                        name="Region"
                        value={editDeveloperData.Region}
                        onChange={handleEditChange}
                        placeholder="Enter region"
                        required
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Status *</label>
                      <select
                        name="Status"
                        value={editDeveloperData.Status}
                        onChange={handleEditChange}
                        required
                        className={styles.select}
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <ClipLoader size={20} color="#fff" />
                        Updating...
                      </>
                    ) : (
                      "Update Developer"
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.developersContainer}>
        {isSubmitting && developers.length === 0 && (
          <div className={styles.loading}>
            <ClipLoader size={30} color="#007bff" />
            <p>Loading developers...</p>
          </div>
        )}

        {filteredDevelopers.length === 0 && !isSubmitting ? (
          <p className={styles.noResults}>
            {searchQuery ? "No developers found matching your search." :
              developers.length === 0 ? "No developers found." : "No developers match your search."}
          </p>
        ) : (
          <div className={styles.developersGrid}>
            {filteredDevelopers.map((developer, index) => (
              <motion.div
                key={developer._id}
                className={styles.developerCard}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className={styles.cardHeader}>
                  <h4>{developer.name}</h4>
                  <div className={styles.cardActions}>
                    {role !== "participant" && (
                      <>
                        <AiOutlineEdit
                          size={20}
                          className={styles.editIcon}
                          onClick={() => handleEdit(developer)}
                          title="Edit Developer"
                        />
                        <AiOutlineDelete
                          size={20}
                          className={styles.deleteIcon}
                          onClick={() => handleDelete(developer._id)}
                          title="Delete Developer"
                        />
                      </>
                    )}
                    <span className={styles.statusBadge}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        className={developer.Status === "Active" ? styles.activeDot : styles.inactiveDot}
                      >
                        <circle cx="5" cy="5" r="5" />
                      </svg>
                      {developer.Status}
                    </span>
                  </div>
                </div>
                <div className={styles.cardContent}>
                  <p><strong>Email:</strong> {developer.email}</p>
                  <p><strong>Phone:</strong> {developer.phoneNumber || developer.phonenumber || "N/A"}</p>
                  <p><strong>Region:</strong> {developer.Region}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Developer;